import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  TicketCheck, AlertCircle, Clock, CheckCircle2, TrendingUp,
  Headset, PhoneCall, ArrowLeft,
} from "lucide-react";
import { format, isToday, subDays } from "date-fns";
import { ar } from "date-fns/locale";
import { useNavigate } from "react-router-dom";

const statusColors: Record<string, string> = {
  open: "bg-blue-100 text-blue-800",
  in_progress: "bg-amber-100 text-amber-800",
  resolved: "bg-green-100 text-green-800",
  closed: "bg-muted text-muted-foreground",
};

const statusLabels: Record<string, string> = {
  open: "مفتوحة",
  in_progress: "قيد المعالجة",
  resolved: "تم الحل",
  closed: "مغلقة",
};

const priorityLabels: Record<string, string> = {
  low: "منخفضة",
  medium: "متوسطة",
  high: "عالية",
  urgent: "عاجلة",
};

const priorityColors: Record<string, string> = {
  urgent: "text-destructive font-bold",
  high: "text-amber-600 font-medium",
  medium: "text-blue-600",
  low: "text-muted-foreground",
};

const SupportDashboard = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const { data: tickets } = useQuery({
    queryKey: ["support-dashboard-tickets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("*, nurseries:nursery_id(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;

      const userIds = [...new Set((data || []).map((t: any) => t.created_by))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", userIds);
      const profileMap = new Map(profiles?.map((p: any) => [p.user_id, p.full_name]) || []);

      return (data || []).map((t: any) => ({
        ...t,
        creator_name: profileMap.get(t.created_by) || "مستخدم",
      }));
    },
  });

  const stats = {
    total: tickets?.length || 0,
    open: tickets?.filter((t: any) => t.status === "open").length || 0,
    inProgress: tickets?.filter((t: any) => t.status === "in_progress").length || 0,
    resolved: tickets?.filter((t: any) => t.status === "resolved" || t.status === "closed").length || 0,
    todayNew: tickets?.filter((t: any) => isToday(new Date(t.created_at))).length || 0,
    urgent: tickets?.filter((t: any) => t.priority === "urgent" && t.status !== "resolved" && t.status !== "closed").length || 0,
    myAssigned: tickets?.filter((t: any) => t.assigned_to === user?.id && t.status !== "resolved" && t.status !== "closed").length || 0,
  };

  const recentTickets = tickets?.slice(0, 8) || [];
  const urgentTickets = tickets?.filter((t: any) =>
    (t.priority === "urgent" || t.priority === "high") && t.status !== "resolved" && t.status !== "closed"
  ).slice(0, 5) || [];

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold">مرحباً، {profile?.full_name || "فريق الدعم"} 👋</h1>
        <p className="text-muted-foreground text-sm mt-1">ملخص حالة التذاكر والدعم الفني</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-r-4 border-r-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">تذاكر مفتوحة</p>
                <p className="text-3xl font-bold text-blue-600">{stats.open}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-r-4 border-r-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">قيد المعالجة</p>
                <p className="text-3xl font-bold text-amber-600">{stats.inProgress}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-r-4 border-r-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">تم حلها</p>
                <p className="text-3xl font-bold text-green-600">{stats.resolved}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-r-4 border-r-destructive">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">عاجلة</p>
                <p className="text-3xl font-bold text-destructive">{stats.urgent}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-destructive/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800">
          <CardContent className="p-4 flex items-center gap-3">
            <Headset className="w-6 h-6 text-teal-600" />
            <div>
              <p className="text-sm font-medium">المعيّنة لي</p>
              <p className="text-2xl font-bold text-teal-700 dark:text-teal-300">{stats.myAssigned}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800">
          <CardContent className="p-4 flex items-center gap-3">
            <TicketCheck className="w-6 h-6 text-indigo-600" />
            <div>
              <p className="text-sm font-medium">تذاكر اليوم</p>
              <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">{stats.todayNew}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
          <CardContent className="p-4 flex items-center gap-3">
            <PhoneCall className="w-6 h-6 text-purple-600" />
            <div>
              <p className="text-sm font-medium">إجمالي التذاكر</p>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Urgent Tickets */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              تذاكر تحتاج انتباه فوري
            </CardTitle>
          </CardHeader>
          <CardContent>
            {urgentTickets.length === 0 ? (
              <p className="text-center py-6 text-muted-foreground text-sm">لا توجد تذاكر عاجلة 🎉</p>
            ) : (
              <div className="space-y-2">
                {urgentTickets.map((t: any) => (
                  <div
                    key={t.id}
                    className="p-3 rounded-lg border border-destructive/20 bg-destructive/5 cursor-pointer hover:bg-destructive/10 transition-colors"
                    onClick={() => navigate("/support-panel/tickets")}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">#{t.ticket_number} — {t.subject}</span>
                      <span className={`text-xs ${priorityColors[t.priority]}`}>{priorityLabels[t.priority]}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">{t.creator_name}</span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(t.created_at), "dd MMM HH:mm", { locale: ar })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Tickets */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <TicketCheck className="w-5 h-5" />
              آخر التذاكر
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/support-panel/tickets")} className="gap-1 text-xs">
              عرض الكل
              <ArrowLeft className="w-3 h-3" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentTickets.map((t: any) => (
                <div
                  key={t.id}
                  className="p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate("/support-panel/tickets")}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm truncate max-w-[60%]">#{t.ticket_number} — {t.subject}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${statusColors[t.status]}`}>
                      {statusLabels[t.status]}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground">{t.creator_name}</span>
                    <span className="text-xs text-muted-foreground">•</span>
                    <Badge variant="outline" className="text-[10px] h-4">{(t as any).nurseries?.name || "—"}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SupportDashboard;
